import type { Channel, Clarification } from "../api/types.js";
import type { SnapNode } from "../lib/stream-snapshot.js";
import type { ReadChip } from "./clarify.js";

/**
 * QA-04 (Mario, 13 Oct, P2; the coordinator's expectation, 14 Oct): a reload keeps the session. This TAB remembers who is
 * signed in — the customer, the session's double-submit token (the login's answer is the only one that carries it; the
 * cookie survives a reload, the token in memory does not), whether the deployment runs demo mode — where they were, judge
 * mode, and what the chat showed. In the tab's own session storage: it goes when the tab closes, at Sign out, and at the
 * next sign-in (a fresh conversation). The bank decides whether the session is still good (GET /v1/session) before
 * anything signed-in is drawn.
 *
 * What it reads back it checks: a record it does not recognise is no record (and is removed). It never throws — no storage
 * (a private window, a blocked or full one) only means a reload signs in again, as before.
 */
export const RESUME_KEY = "dcta.resume";
export const CHAT_KEY = "dcta.chat";

/** The screens a reload comes back to; a screen over one of them comes back as the one under it. */
export type Place = "home" | "activity" | "invest" | "me" | "chat";
const PLACES: ReadonlySet<string> = new Set(["home", "activity", "invest", "me", "chat"]);
export const isPlace = (screen: string): screen is Place => PLACES.has(screen);

export interface Resume {
  readonly customerId: string;
  readonly csrf: string;
  readonly demo: boolean;
  readonly place: Place;
  readonly judge: boolean;
}

/** The words that started a turn and how they came — "Ask again" and a restored card's own ways on send them back. */
export interface KeptWords {
  readonly said: string;
  readonly channel: Channel;
}

/**
 * What the chat showed when the page went away (app/chat.ts `kept`): the past as data (lib/stream-snapshot.ts); the turn in
 * flight (`pending`); the cards in play, by id, to read again from the bank (`live`); the open questions, to draw again live
 * (`open`); the path the bank drafts on; whether the voice-clip line was shown (once a session); unsent words in the field.
 */
export interface ChatKept {
  readonly customerId: string;
  readonly nodes: readonly SnapNode[];
  readonly pending?: KeptWords;
  readonly live?: readonly (KeptWords & { readonly id: string })[];
  readonly open?: KeptWords & { readonly draftId: string; readonly questions: readonly Clarification[]; readonly read: readonly ReadChip[] };
  readonly path?: "model" | "fixture";
  readonly consent?: boolean;
  readonly field?: string;
}

const CUSTOMER = /^cus_[a-z0-9]{1,32}$/;
/** A bundle or draft id goes back into a path (encoded): letters, digits, `_` and `-` only. */
const ID = /^[A-Za-z0-9_-]{1,80}$/;
const WORDS_MAX = 4000;
/** Past this, the newest half of the conversation is tried, then a quarter… then nothing. */
const CHAT_MAX = 1_500_000;

const isObject = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
const isWords = (v: unknown): v is string => typeof v === "string" && v.trim() !== "" && v.length <= WORDS_MAX;
const isChannel = (v: unknown): v is Channel => v === "typed" || v === "voice";

function wordsOf(v: unknown): KeptWords | undefined {
  if (!isObject(v) || !isWords(v["said"]) || !isChannel(v["channel"])) return undefined;
  return { said: v["said"], channel: v["channel"] };
}

function questionOf(v: unknown): Clarification | undefined {
  if (!isObject(v) || typeof v["question_id"] !== "string" || typeof v["reason_code"] !== "string") return undefined;
  if (v["question_text"] !== undefined && typeof v["question_text"] !== "string") return undefined;
  const choices = v["choices"];
  if (choices !== undefined && (!Array.isArray(choices) || !choices.every((c) => isObject(c) && typeof c["choice_id"] === "string" && typeof c["label"] === "string"))) return undefined;
  return v as unknown as Clarification;
}

function openOf(v: unknown): ChatKept["open"] {
  const words = wordsOf(v);
  if (words === undefined || !isObject(v) || typeof v["draftId"] !== "string" || !ID.test(v["draftId"])) return undefined;
  const qs = v["questions"];
  const read = v["read"];
  if (!Array.isArray(qs) || qs.length === 0 || !Array.isArray(read)) return undefined;
  const questions = qs.map(questionOf);
  if (questions.some((q) => q === undefined)) return undefined;
  if (!read.every((r) => isObject(r) && typeof r["label"] === "string" && typeof r["words"] === "string")) return undefined;
  return { ...words, draftId: v["draftId"], questions: questions as Clarification[], read: read as ReadChip[] };
}

/** A kept chat as it was written, every part it does not recognise left out. */
function chatOf(v: unknown): ChatKept | undefined {
  if (!isObject(v) || v["v"] !== 1 || typeof v["customerId"] !== "string" || !CUSTOMER.test(v["customerId"]) || !Array.isArray(v["nodes"])) return undefined;
  const pending = wordsOf(v["pending"]);
  const live = Array.isArray(v["live"])
    ? v["live"].flatMap((l: unknown) => {
        const words = wordsOf(l);
        return words !== undefined && isObject(l) && typeof l["id"] === "string" && ID.test(l["id"]) ? [{ ...words, id: l["id"] }] : [];
      })
    : [];
  const open = openOf(v["open"]);
  const path = v["path"] === "model" || v["path"] === "fixture" ? v["path"] : undefined;
  const field = typeof v["field"] === "string" && v["field"] !== "" && v["field"].length <= WORDS_MAX ? v["field"] : undefined;
  return {
    customerId: v["customerId"],
    nodes: v["nodes"] as SnapNode[],
    ...(pending === undefined ? {} : { pending }),
    ...(live.length === 0 ? {} : { live }),
    ...(open === undefined ? {} : { open }),
    ...(path === undefined ? {} : { path }),
    ...(v["consent"] === true ? { consent: true } : {}),
    ...(field === undefined ? {} : { field }),
  };
}

function resumeOf(v: unknown): Resume | undefined {
  if (!isObject(v) || v["v"] !== 1) return undefined;
  const { customerId, csrf, demo, place, judge } = v;
  if (typeof customerId !== "string" || !CUSTOMER.test(customerId)) return undefined;
  if (typeof csrf !== "string" || csrf === "" || csrf.length > 512) return undefined;
  if (typeof demo !== "boolean" || typeof judge !== "boolean" || typeof place !== "string" || !isPlace(place)) return undefined;
  return { customerId, csrf, demo, place, judge };
}

export class ResumeStore {
  readonly #storage: () => Storage | undefined;

  constructor(storage: () => Storage | undefined) {
    this.#storage = storage;
  }

  /** A sign-in: kept, on Home, judge mode off — and the conversation kept before it goes (a fresh one). */
  start(customerId: string, csrf: string, demo: boolean): void {
    this.#remove(CHAT_KEY);
    this.#write(RESUME_KEY, { v: 1, customerId, csrf, demo, place: "home", judge: false });
  }

  read(): Resume | undefined {
    return this.#read(RESUME_KEY, resumeOf);
  }

  /** Where the customer is now, and judge mode. Nothing kept (signed out): nothing written. */
  note(place: Place, judge: boolean): void {
    const now = this.read();
    if (now !== undefined) this.#write(RESUME_KEY, { v: 1, ...now, place, judge });
  }

  /** What the chat showed. Too much for the quota: its newest half, then a quarter… then nothing (never a stale one). */
  keepChat(chat: ChatKept): void {
    let nodes = chat.nodes;
    for (let tries = 0; tries < 8; tries += 1) {
      const json = JSON.stringify({ v: 1, ...chat, nodes });
      if (json.length <= CHAT_MAX && this.#write(CHAT_KEY, json)) return;
      if (nodes.length === 0) break;
      nodes = nodes.slice(Math.ceil(nodes.length / 2));
    }
    this.#remove(CHAT_KEY);
  }

  /** The conversation this customer's session showed in this tab — anyone else's is not theirs. */
  chat(customerId: string): ChatKept | undefined {
    const kept = this.#read(CHAT_KEY, chatOf);
    return kept?.customerId === customerId ? kept : undefined;
  }

  /** Sign out, or a session the bank no longer holds. */
  forget(): void {
    this.#remove(RESUME_KEY);
    this.#remove(CHAT_KEY);
  }

  #store(): Storage | undefined {
    try {
      return this.#storage();
    } catch {
      return undefined;
    }
  }

  #read<T>(key: string, check: (v: unknown) => T | undefined): T | undefined {
    const s = this.#store();
    if (s === undefined) return undefined;
    try {
      const raw = s.getItem(key);
      if (raw === null) return undefined;
      const value = check(JSON.parse(raw));
      if (value === undefined) s.removeItem(key);
      return value;
    } catch {
      this.#remove(key);
      return undefined;
    }
  }

  #write(key: string, value: unknown): boolean {
    const s = this.#store();
    if (s === undefined) return false;
    try {
      s.setItem(key, typeof value === "string" ? value : JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  }

  #remove(key: string): void {
    try {
      this.#store()?.removeItem(key);
    } catch {
      // nothing kept, nothing to remove
    }
  }
}
